/**
 * Frontend Error Reporting Service
 *
 * Captures frontend errors (React crashes, unhandled exceptions, promise rejections)
 * and sends them to the backend telemetry endpoint, which forwards to:
 * 1. Azure Application Insights (if enabled)
 * 2. ELK Stack (via stdout logging)
 *
 * Security:
 * - All errors are sent to backend proxy (connection strings stay server-side)
 * - Authentication required (only logged-in users can report)
 * - Rate limited on backend (10 errors/minute per user)
 *
 * Usage:
 *   import { errorReporting } from '@/services/errorReporting';
 *
 *   // Report an error
 *   errorReporting.report(error, 'ComponentName');
 *
 *   // Report with extra context
 *   errorReporting.report(error, 'ComponentName', { action: 'save', entityId: '123' });
 */

import { config } from '../config'
import { createServiceFetch } from '../utils/validateFetchUrl'

const errorReportFetch = createServiceFetch(config.api.baseUrl)
import { logger } from '../utils/logger'
import { isBenignFrontendError } from '../utils/benignFrontendErrors'
import { captureBufferPushError } from '../features/bug-report/captureBuffer'
import { maybeReportAutoBug } from '../features/bug-report/autoReporter'

// =============================================================================
// Types
// =============================================================================

interface ErrorReport {
  message: string
  stack?: string
  url: string
  component?: string
  timestamp: string
  user_agent: string
  extra?: Record<string, unknown>
}

interface ErrorBatch {
  errors: ErrorReport[]
}

// =============================================================================
// Configuration
// =============================================================================

const ERROR_REPORTING_CONFIG = {
  /** Maximum errors to queue before dropping oldest */
  MAX_QUEUE_SIZE: 50,
  /** Maximum errors to send in a single batch */
  BATCH_SIZE: 10,
  /** Debounce interval before sending queued errors (ms) */
  FLUSH_DEBOUNCE: 2000,
  /** Endpoint for error reporting (appended to config.api.baseUrl which is already /api) */
  ENDPOINT: '/v1/telemetry/errors/batch',
  /** Single error endpoint (for immediate reports) */
  SINGLE_ENDPOINT: '/v1/telemetry/errors',
  /** Maximum consecutive send failures before dropping the queue (prevents infinite retry loops) */
  MAX_CONSECUTIVE_FAILURES: 3,
}

// =============================================================================
// Error Reporting Service
// =============================================================================

class ErrorReportingService {
  private queue: ErrorReport[] = []
  private isSending = false
  private flushTimeout: ReturnType<typeof setTimeout> | null = null
  private isEnabled: boolean
  private consecutiveFailures = 0

  constructor() {
    // Disable error reporting in development by default (too noisy)
    // Can be overridden via config.features.enableErrorReporting
    this.isEnabled = !config.env.isDevelopment
  }

  /**
   * Enable or disable error reporting at runtime.
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled
    logger.info(`Error reporting ${enabled ? 'enabled' : 'disabled'}`)
  }

  /**
   * Report an error to the backend telemetry service.
   *
   * @param error - The error to report
   * @param component - Optional component name where error occurred
   * @param extra - Optional additional context
   */
  report(error: Error | unknown, component?: string, extra?: Record<string, unknown>): void {
    if (!this.isEnabled) {
      return
    }

    // Don't report errors if user is not authenticated
    const token = this.getAuthToken()
    if (!token) {
      logger.debug('Skipping error report - user not authenticated')
      return
    }

    const errorObj = error instanceof Error ? error : new Error(String(error))

    if (isBenignFrontendError(errorObj.message)) {
      return
    }

    const report: ErrorReport = {
      message: this.sanitizeMessage(errorObj.message),
      stack: this.sanitizeStack(errorObj.stack),
      url: window.location.href,
      component,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      extra: this.sanitizeExtra(extra),
    }

    this.queue.push(report)

    // Prevent queue overflow by dropping oldest
    if (this.queue.length > ERROR_REPORTING_CONFIG.MAX_QUEUE_SIZE) {
      this.queue.shift()
      logger.warn('Error queue overflow - dropping oldest error')
    }

    // Debounce flush
    this.scheduleFlush()
  }

  /**
   * Report an error immediately without batching.
   * Use for critical errors that must be reported ASAP.
   */
  async reportImmediate(
    error: Error | unknown,
    component?: string,
    extra?: Record<string, unknown>
  ): Promise<void> {
    if (!this.isEnabled) {
      return
    }

    const token = this.getAuthToken()
    if (!token) {
      return
    }

    const errorObj = error instanceof Error ? error : new Error(String(error))

    if (isBenignFrontendError(errorObj.message)) {
      return
    }

    const report: ErrorReport = {
      message: this.sanitizeMessage(errorObj.message),
      stack: this.sanitizeStack(errorObj.stack),
      url: window.location.href,
      component,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      extra: this.sanitizeExtra(extra),
    }

    try {
      await this.sendSingleError(report, token)
    } catch (e) {
      // Silent fail - don't crash app due to telemetry
      logger.debug('Failed to report error immediately', { error: e })
    }
  }

  /**
   * Flush all queued errors now.
   */
  async flush(): Promise<void> {
    if (this.isSending || this.queue.length === 0) {
      return
    }

    const token = this.getAuthToken()
    if (!token) {
      return
    }

    this.isSending = true

    // Take up to BATCH_SIZE errors
    const batch = this.queue.splice(0, ERROR_REPORTING_CONFIG.BATCH_SIZE)

    try {
      await this.sendBatch(batch, token)
      this.consecutiveFailures = 0
    } catch (e) {
      this.consecutiveFailures++

      if (this.consecutiveFailures >= ERROR_REPORTING_CONFIG.MAX_CONSECUTIVE_FAILURES) {
        // Drop the queue to prevent infinite retry loops (e.g. persistent 404)
        logger.debug(
          `Error reporting failed ${this.consecutiveFailures} times consecutively, dropping ${batch.length + this.queue.length} queued errors`
        )
        this.queue = []
        this.consecutiveFailures = 0
      } else {
        // Put errors back in queue for retry (at the front)
        this.queue.unshift(...batch)
        logger.debug('Failed to send error batch, will retry', { error: e })
      }
    } finally {
      this.isSending = false

      // If more errors remain, schedule another flush
      if (this.queue.length > 0) {
        this.scheduleFlush()
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Private Methods
  // ---------------------------------------------------------------------------

  private scheduleFlush(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout)
    }

    this.flushTimeout = setTimeout(() => {
      this.flush()
    }, ERROR_REPORTING_CONFIG.FLUSH_DEBOUNCE)
  }

  private getAuthToken(): string | null {
    try {
      return localStorage.getItem(config.auth.tokenKey)
    } catch {
      return null
    }
  }

  private getTenantId(): string | null {
    try {
      return localStorage.getItem('tagp-tenant-id')
    } catch {
      return null
    }
  }

  private async sendBatch(errors: ErrorReport[], token: string): Promise<void> {
    const payload: ErrorBatch = { errors }
    const tenantId = this.getTenantId()

    const response = await errorReportFetch(`${config.api.baseUrl}${ERROR_REPORTING_CONFIG.ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(tenantId && { 'X-Tenant-ID': tenantId }),
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`Error reporting failed: ${response.status}`)
    }
  }

  private async sendSingleError(report: ErrorReport, token: string): Promise<void> {
    const tenantId = this.getTenantId()

    const response = await errorReportFetch(`${config.api.baseUrl}${ERROR_REPORTING_CONFIG.SINGLE_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(tenantId && { 'X-Tenant-ID': tenantId }),
      },
      body: JSON.stringify(report),
    })

    if (!response.ok) {
      throw new Error(`Error reporting failed: ${response.status}`)
    }
  }

  /**
   * Sanitize error message to remove potential PII.
   */
  private sanitizeMessage(message: string): string {
    if (!message) return 'Unknown error'

    // Truncate to max length
    let sanitized = message.substring(0, 2000)

    // Remove potential email addresses
    sanitized = sanitized.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL]')

    // Remove potential UUIDs (keep first 8 chars for debugging)
    sanitized = sanitized.replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      (match) => match.substring(0, 8) + '...'
    )

    // Remove potential JWT tokens
    sanitized = sanitized.replace(/eyJ[a-zA-Z0-9_-]*\.eyJ[a-zA-Z0-9_-]*\.[a-zA-Z0-9_-]*/g, '[JWT]')

    return sanitized
  }

  /**
   * Sanitize stack trace.
   */
  private sanitizeStack(stack?: string): string | undefined {
    if (!stack) return undefined

    // Truncate to max length
    let sanitized = stack.substring(0, 10000)

    // Remove webpack hash noise (common in minified code)
    sanitized = sanitized.replace(/[a-f0-9]{20,}/gi, '[HASH]')

    return sanitized
  }

  /**
   * Sanitize extra context to prevent PII leakage.
   */
  private sanitizeExtra(extra?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!extra) return undefined

    const sanitized: Record<string, unknown> = {}
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'credential', 'auth', 'email']

    // Only take first 10 keys
    const keys = Object.keys(extra).slice(0, 10)

    for (const key of keys) {
      // Skip sensitive keys
      if (sensitiveKeys.some((sk) => key.toLowerCase().includes(sk))) {
        sanitized[key] = '[REDACTED]'
        continue
      }

      const value = extra[key]

      // Stringify and truncate values
      if (value === null || value === undefined) {
        sanitized[key] = null
      } else if (typeof value === 'object') {
        try {
          sanitized[key] = JSON.stringify(value).substring(0, 500)
        } catch {
          sanitized[key] = '[Object]'
        }
      } else {
        sanitized[key] = String(value).substring(0, 500)
      }
    }

    return sanitized
  }
}

// =============================================================================
// Singleton Export
// =============================================================================

export const errorReporting = new ErrorReportingService()

// =============================================================================
// Global Error Handlers Setup
// =============================================================================

/**
 * Initialize global error handlers.
 * Call this once in main.tsx or App.tsx.
 */
export function setupGlobalErrorHandlers(): void {
  // Handle uncaught errors
  window.onerror = (
    message: string | Event,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error
  ): boolean => {
    const errorToReport = error || new Error(String(message))
    if (isBenignFrontendError(errorToReport.message) || isBenignFrontendError(message)) {
      return true
    }

    errorReporting.report(errorToReport, 'window.onerror', {
      source,
      lineno,
      colno,
    })
    try {
      captureBufferPushError(errorToReport, 'window.onerror', { source, lineno, colno })
      void maybeReportAutoBug(errorToReport, 'window.onerror')
    } catch {
      /* ignore */
    }

    // Don't prevent default error handling
    return false
  }

  // Handle unhandled promise rejections
  window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    if (isBenignFrontendError(error.message)) {
      return
    }

    errorReporting.report(error, 'unhandledrejection', {
      type: 'promise_rejection',
    })
    try {
      captureBufferPushError(error, 'unhandledrejection', { type: 'promise_rejection' })
      void maybeReportAutoBug(error, 'unhandledrejection')
    } catch {
      /* ignore */
    }
  }

  logger.info('Global error handlers initialized')
}
